import { BASEURL } from '../../utilites';
import axios from 'axios';
import { REVENUE_ACTION_TYPE } from './Type';

/**
 * Fetch transactions for the dashboard period (with optional date range).
 * Dispatches period data; optionally also fetches all-time total.
 */
const getRevenueTransactions = (fromDate, toDate) => {
    return async (dispatch) => {
        dispatch({ type: REVENUE_ACTION_TYPE.setLoading, payload: true });
        try {
            // Using native Date to format if dates exist, otherwise sending 'none' for All Time case
            const params = {
                sortBy: 'created_at',
                order: 'DESC',
                page: fromDate ? 1 : 'none',
                perPage: fromDate ? 500 : 'none',
            };

            if (fromDate) {
                // Native Date formatting (YYYY-MM-DD)
                params.from_date = new Date(fromDate).toISOString().split('T')[0];
                params.to_date = new Date(toDate).toISOString().split('T')[0];
            }

            // Single API Call
            const response = await axios.get(`${BASEURL}/api/transactions/list`, { params });
            const respData = response?.data?.data;

            const dataList = respData?.data || [];
            const totalAmount = respData?.totalAmount || 0;
            const previousAmount = respData?.previousPeriodAmount || 0;
            const jobs = respData?.wholeJobs || []

            dispatch({
                type: REVENUE_ACTION_TYPE.setAllJobs,
                payload: jobs,
            });

            // Update Period Data (Works for both Specific Period and All Time)
            dispatch({
                type: REVENUE_ACTION_TYPE.setPeriodData,
                payload: {
                    data: dataList,
                    totalAmount: totalAmount,
                    count: respData?.count ?? dataList.length,
                },
            });

            // Update Previous Period Data
            dispatch({
                type: REVENUE_ACTION_TYPE.setPreviousPeriodData,
                payload: { totalAmount: previousAmount },
            });

             dispatch({
                type: REVENUE_ACTION_TYPE.setAllUsers,
                payload: respData?.allUsers ?? [] ,
            });

            dispatch({
                type: REVENUE_ACTION_TYPE.setAllTaxPros,
                payload: respData?.allTaxPros ?? [] ,
            });

            // Update All Time Stats (Only if it was an All Time request)
            if (!fromDate) {
                dispatch({
                    type: REVENUE_ACTION_TYPE.setAllTimeData,
                    payload: { totalAmount: totalAmount , completedJobsCount: dataList.length ?? 0 , data : dataList },
                });
            }

        } catch (err) {
            dispatch({
                type: REVENUE_ACTION_TYPE.setPeriodData,
                payload: { data: [], totalAmount: 0, count: 0 },
            });
            dispatch({
                type: REVENUE_ACTION_TYPE.setAllTimeData,
                payload: { totalAmount: 0 },
            });
            dispatch({
                type: REVENUE_ACTION_TYPE.setPreviousPeriodData,
                payload: { totalAmount: 0 },
            });
        }
        dispatch({ type: REVENUE_ACTION_TYPE.setLoading, payload: false });
    };
};

/**
 * Fetch full transaction list for Excel export. Stores in state.revenue.exportData.
 */
const getRevenueTransactionsForExport = () => {
	return async (dispatch) => {
		try {
			const resp = await axios.get(`${BASEURL}/api/transactions/list`, {
				params: { page: 'none', perPage: 'none', sortBy: 'created_at', order: 'DESC' },
			});
			const list = resp?.data?.data?.data ?? [];
			dispatch({ type: REVENUE_ACTION_TYPE.setExportData, payload: list });
		} catch (err) {
			dispatch({ type: REVENUE_ACTION_TYPE.setExportData, payload: [] });
		}
	};
};

const extendGracePeriodByAdmin = (payload) => {
  return async (dispatch) => {
    dispatch({ type: REVENUE_ACTION_TYPE.setLoading, payload: true });

    try {
      const response = await axios.post(
        `${BASEURL}/api/membership/update-membership-by-admin`,
        payload
      );

      const message = response?.data?.message || "Updated successfully";

      dispatch({
        type: REVENUE_ACTION_TYPE.extendGracePeriodByAdmin,
        payload: message,
      });

    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";

      dispatch({
        type: REVENUE_ACTION_TYPE.extendGracePeriodByAdmin,
        payload: errorMessage,
      });
    }

    dispatch({ type: REVENUE_ACTION_TYPE.setLoading, payload: false });
  };
};

const clearRevenueExportData = () => ({ type: REVENUE_ACTION_TYPE.clearExportData });

const BATCH_SIZE = 10;

const syncAllFromStripe = (onProgress) => {
    return async (dispatch) => {
        dispatch({
            type: REVENUE_ACTION_TYPE.syncAllFromStripe,
            payload: { loading: true, data: null, error: null },
        });
        try {
            // Step 1: Fetch all customer IDs
            if (onProgress) onProgress({ step: 'customers', message: 'Fetching Stripe customers...' });
            const customersRes = await axios.post(`${BASEURL}/api/membership/sync-stripe-fetch-customers`);
            const { customers, total } = customersRes?.data?.data;

            const summary = {
                customersProcessed: 0,
                subscriptionsUpdated: 0,
                subscriptionsCreated: 0,
                invoicesSynced: 0,
                skippedNoUser: 0,
                customersLinked: 0,
                errors: [],
                total,
            };

            // Step 2: Process in batches
            for (let i = 0; i < customers.length; i += BATCH_SIZE) {
                const batch = customers.slice(i, i + BATCH_SIZE);
                const processed = Math.min(i + BATCH_SIZE, customers.length);

                if (onProgress) onProgress({
                    step: 'syncing',
                    message: `Syncing customers ${processed} of ${total}...`,
                    progress: Math.round((processed / total) * 100),
                });

                const batchRes = await axios.post(`${BASEURL}/api/membership/sync-stripe-batch`, { customers: batch });
                const batchData = batchRes?.data?.data;

                if (batchData) {
                    summary.customersProcessed += batchData.customersProcessed || 0;
                    summary.subscriptionsUpdated += batchData.subscriptionsUpdated || 0;
                    summary.subscriptionsCreated += batchData.subscriptionsCreated || 0;
                    summary.invoicesSynced += batchData.invoicesSynced || 0;
                    summary.skippedNoUser += batchData.skippedNoUser || 0;
                    summary.customersLinked += batchData.customersLinked || 0;
                    if (batchData.errors?.length) summary.errors.push(...batchData.errors);
                }
            }

            if (onProgress) onProgress({ step: 'done', message: 'Sync complete', progress: 100 });

            dispatch({
                type: REVENUE_ACTION_TYPE.syncAllFromStripe,
                payload: { loading: false, data: summary, error: null },
            });
            return { success: true, data: summary };
        } catch (err) {
            const errorMessage = err?.response?.data?.error || 'Sync failed';
            dispatch({
                type: REVENUE_ACTION_TYPE.syncAllFromStripe,
                payload: { loading: false, data: null, error: errorMessage },
            });
            return { success: false, message: errorMessage };
        }
    };
};

const syncSingleFromStripe = (identifier) => {
    return async () => {
        try {
            const payload = identifier.includes('@')
                ? { email: identifier.trim() }
                : { customer_id: identifier.trim() };

            const response = await axios.post(
                `${BASEURL}/api/membership/sync-stripe-single`,
                payload
            );

            return { success: true, data: response?.data?.data };
        } catch (err) {
            const errorMessage = err?.response?.data?.error || 'Sync failed';
            return { success: false, message: errorMessage };
        }
    };
};

const createStripeCustomer = (userId, email, name) => {
    return async () => {
        try {
            const response = await axios.post(
                `${BASEURL}/api/membership/create-stripe-customer`,
                { userId, email, name }
            );
            return { success: true, data: response?.data?.data };
        } catch (err) {
            return { success: false, message: err?.response?.data?.error || 'Failed to create Stripe customer' };
        }
    };
};

const confirmStripeActivation = (activations) => {
    return async () => {
        try {
            const response = await axios.post(
                `${BASEURL}/api/membership/confirm-stripe-activation`,
                { activations }
            );
            return { success: true, data: response?.data?.data };
        } catch (err) {
            return { success: false, message: err?.response?.data?.error || 'Activation failed' };
        }
    };
};

export const revenue = {
	getRevenueTransactions,
	getRevenueTransactionsForExport,
	clearRevenueExportData,
    extendGracePeriodByAdmin,
    syncAllFromStripe,
    syncSingleFromStripe,
    confirmStripeActivation,
    createStripeCustomer
};
